import { Columns, DateType } from '../types/reports';

export const APPT_TABLE_COL_WIDTH = {
  sm: 100,
  md: 150,
  lg: 170,
};

const TRANSFER_TOOLTIP =
  'This amount only includes credit payments and will always include tip and taxes. The cost of any retail items that were purchased along with the appointment will be included in this amount.';
const VOIDED_TOOLTIP =
  'This includes amounts for cash and gift card payments that have been voided.';

export const APPT_TABLE_COLUMNS = [
  {
    label: 'Artist',
    width: APPT_TABLE_COL_WIDTH.md,
    dataKey: 'artist',
  },
  {
    label: 'Customer',
    width: APPT_TABLE_COL_WIDTH.md,
    dataKey: 'customer',
  },
  {
    label: 'Apt. Date',
    width: APPT_TABLE_COL_WIDTH.md,
    dataKey: 'date',
  },
  {
    label: 'Deposit Paid',
    width: 'auto',
    dataKey: 'deposit',
    dollar: true,
  },
  {
    label: 'Payment Method',
    width: 'auto',
    dataKey: 'depositPaymentMethod',
  },
  {
    label: 'Paid On',
    width: APPT_TABLE_COL_WIDTH.md,
    dataKey: 'depositPaymentDate',
  },
  {
    label: 'Remaining Amount Paid',
    width: 'auto',
    dataKey: 'servicePricePaid',
    dollar: true,
  },
  {
    label: 'Payment Method',
    width: 'auto',
    dataKey: 'paymentMethod',
  },
  {
    label: 'Tip Amount',
    width: 'auto',
    dataKey: 'tipAmount',
    dollar: true,
  },
  {
    label: 'Paid On',
    width: APPT_TABLE_COL_WIDTH.md,
    dataKey: 'checkoutCompletedOn',
  },
  {
    label: 'Total Cash',
    width: 'auto',
    dataKey: 'totalCash',
    dollar: true,
  },
  {
    label: 'Total Credit',
    width: 'auto',
    dataKey: 'totalCredit',
    dollar: true,
  },
  {
    label: 'Total Gift Card',
    width: 'auto',
    dataKey: 'totalGiftCard',
    dollar: true,
  },
  {
    label: 'Sales Tax',
    width: 'auto',
    dataKey: 'salesTax',
    dollar: true,
    tooltip: 'This is the sum of all sales tax collected for this appointment.',
  },
  {
    label: 'Refunded Amount',
    width: 'auto',
    dataKey: 'refundedAmount',
    dollar: true,
  },
  {
    label: 'Voided Amount',
    width: 'auto',
    dataKey: 'voidedAmount',
    dollar: true,
    tooltip: VOIDED_TOOLTIP,
  },
  {
    label: 'Amount Sent to Artist',
    width: 'auto',
    dataKey: 'amountSentToArtist',
    dollar: true,
    tooltip: TRANSFER_TOOLTIP,
  },
  {
    label: 'Amount Sent to Shop',
    width: 'auto',
    dataKey: 'amountSentToShop',
    dollar: true,
    tooltip: TRANSFER_TOOLTIP,
  },
  {
    label: 'Total Fees',
    width: 'auto',
    dataKey: 'totalFees',
    dollar: true,
  },
  // Hidden columns used for data processing
  {
    label: 'Appointment ID',
    dataKey: 'appointmentId',
    hidden: true,
  },
  {
    label: 'Total Sales',
    dataKey: 'totalSales',
    hidden: true,
  },
] as const satisfies Columns;

export const ORDER_TABLE_COLUMNS = [
  {
    label: 'Customer',
    width: APPT_TABLE_COL_WIDTH.md,
    dataKey: 'customer',
  },
  {
    label: 'Date',
    width: APPT_TABLE_COL_WIDTH.md,
    dataKey: 'date',
  },
  {
    label: 'Items',
    width: APPT_TABLE_COL_WIDTH.md,
    dataKey: 'items',
  },
  {
    label: 'Amount',
    width: 'auto',
    dataKey: 'amount',
    dollar: true,
  },
  {
    label: 'Payment Method',
    width: 'auto',
    dataKey: 'paymentMethod',
  },
  {
    label: 'Sales Tax',
    width: 'auto',
    dataKey: 'salesTax',
    dollar: true,
    tooltip:
      'This is the sum of all sales tax collected for this retail checkout.',
  },
  {
    label: 'Refunded Amount',
    width: 'auto',
    dataKey: 'refundedAmount',
    dollar: true,
  },
  {
    label: 'Voided Amount',
    width: 'auto',
    dataKey: 'voidedAmount',
    dollar: true,
    tooltip: VOIDED_TOOLTIP,
  },
  // Hidden columns used for data processing
  {
    label: 'Order ID',
    dataKey: 'orderId',
    hidden: true,
  },
  {
    label: 'Payment ID',
    dataKey: 'paymentId',
    hidden: true,
  },
] as const satisfies Columns;

export const WARNING_CONTENT: Record<
  DateType,
  {
    title: string;
    subtitle: string;
  }
> = {
  appointment: {
    title: 'This report is filtered by appointment date.',
    subtitle:
      'Only appointments completed & checked out through Porter during the selected date range will show up. Deposits collected for future appointments will not be included in these reports.',
  },
  payment: {
    title: 'This report is filtered by payment date.',
    subtitle:
      'All appointments with a payment (deposit or Porter appointment checkout) during the selected date range will show up. Deposits collected for future appointments will be included in these reports.',
  },
};

export const TABLE_HEADER_CONTENT: Record<DateType, string> = {
  payment:
    'This report shows all appointments with a payment during the selected date range. For each appointment, you can see the payments made by the customer during the selected date range, and how much money was sent to your account for the appointment.',
  appointment:
    'This report shows all appointments during the selected date range. For each appointment, you can see the payments made by the customer, and how much money was sent to your account for the appointment.',
};

export const RETAIL_CHECKOUTS_TABLE_DESCRIPTION =
  'This report shows all retail checkouts that were completed during the selected date range. For each checkout, you can see the items that were purchased, the payment method used, and the total amount of the checkout.';
